import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { isInIframe } from 'utils/proxyUtils';
import { setPageId, setSelectedTaskId } from './tasksSlice';

export const taskIdMiddleware = createListenerMiddleware();

type TaskUpdateMessage = {
  type:
    | 'UPDATE_TASK_ID'
    | 'REMOVE_TASK_ID'
    | 'UPDATE_TASK_ID'
    | 'REMOVE_PAGE_ID'
    | 'UPDATE_PAGE_ID';
  taskId?: string;
  pageId?: string;
};

// Helper to update URL in shadow root context
const updateUrl = (taskId: string | null) => {
  const url = new URL(window.location.href);
  if (!taskId) {
    url.searchParams.delete('taskId');
    window.history.pushState({}, '', url);
    return;
  }
  if (taskId) {
    url.searchParams.set('taskId', taskId);
  } else {
    url.searchParams.delete('taskId');
  }
  window.history.pushState({}, '', url);
};

const updatePageUrl = (pageId: string | null) => {
  const url = new URL(window.location.href);
  if (!pageId) {
    url.searchParams.delete('pageId');
    window.history.pushState({}, '', url);
    return;
  }
  if (pageId) {
    url.searchParams.set('pageId', pageId);
  } else {
    url.searchParams.delete('pageId');
  }
  window.history.pushState({}, '', url);
};

// Helper to send message to parent in iframe context
const sendMessageToParent = (message: TaskUpdateMessage) => {
  window.parent.postMessage(message, '*');
};

taskIdMiddleware.startListening({
  matcher: isAnyOf(setSelectedTaskId),
  effect: (action) => {
    const taskId = action.payload as string;
    if (
      !taskId ||
      typeof taskId !== 'string' ||
      taskId.includes('PENDING') ||
      taskId.includes('OPTIMISTIC')
    ) {
      if (isInIframe()) {
        // In iframe mode, send message to parent
        sendMessageToParent({
          type: 'REMOVE_TASK_ID',
        });
      } else {
        // In shadow root mode, update URL directly
        updateUrl(null);
      }
    }
    if (
      taskId &&
      !taskId.includes('PENDING') &&
      !taskId.includes('OPTIMISTIC') &&
      typeof taskId === 'string'
    ) {
      if (isInIframe()) {
        // In iframe mode, send message to parent
        sendMessageToParent({
          type: 'UPDATE_TASK_ID',
          taskId: taskId,
        });
      } else {
        // In shadow root mode, update URL directly
        updateUrl(taskId);
      }
    }
  },
});

// Handle page ID changes
taskIdMiddleware.startListening({
  matcher: isAnyOf(setPageId),
  effect: (action) => {
    const pageId = action.payload;
    if (!pageId || typeof pageId !== 'string' || pageId === 'PENDING') {
      if (isInIframe()) {
        // In iframe mode, send message to parent
        sendMessageToParent({
          type: 'REMOVE_PAGE_ID',
        });
      } else {
        // In shadow root mode, update URL directly
        updateUrl(pageId as string | null);
      }
    } else if (pageId !== 'PENDING' && typeof pageId === 'string') {
      if (isInIframe()) {
        // In iframe mode, send message to parent
        sendMessageToParent({
          type: 'UPDATE_PAGE_ID',
          pageId: pageId,
        });
      } else {
        // In shadow root mode, update URL directly
        updatePageUrl(pageId);
      }
    }
  },
});
